import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import type * as React from "react";

// The one v3.2 chip (style.css .chip; iOS ChipLabel): a quiet pill with a
// hairline. As a filter it fills with ink when pressed (aria-pressed or
// data-state=active); "warn" is the amber of values that need review,
// "suggestion" the soft chips above the Ask bar.
//
// apps/web's V3Chip (components/ui/v3/chip.tsx) uses these variants.
export const chipVariants = cva(
  "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full font-v3 font-medium tabular-nums transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v3-brand disabled:pointer-events-none disabled:opacity-40 [&_svg]:size-3.5 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "text-v3-text-2 shadow-v3-hairline-strong hover:bg-v3-surface-2 aria-pressed:bg-v3-ink aria-pressed:text-v3-on-ink aria-pressed:shadow-none data-[state=active]:bg-v3-ink data-[state=active]:text-v3-on-ink data-[state=active]:shadow-none",
        warn: "bg-v3-warn-tint text-v3-warn-text shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--v3-warn)_30%,transparent)] hover:bg-[color-mix(in_srgb,var(--v3-warn)_20%,transparent)] aria-pressed:bg-v3-warn aria-pressed:text-v3-panel",
        suggestion:
          "bg-v3-panel text-v3-text shadow-v3-hairline-strong hover:bg-v3-surface-2",
      },
      size: {
        default: "h-11 px-3.5 text-[13px] lg:h-8 lg:px-3",
        sm: "h-7 px-2.5 text-[12px]",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

type ChipVariantProps = VariantProps<typeof chipVariants>;

// A pressable chip (suggestion, action).
export function Chip({
  variant,
  size,
  className,
  type,
  ...props
}: ChipVariantProps &
  React.ComponentPropsWithRef<"button">): React.JSX.Element {
  return (
    <button
      className={cn(chipVariants({ variant, size }), className)}
      data-slot="chip"
      type={type ?? "button"}
      {...props}
    />
  );
}

// A filter: ink while pressed.
export function FilterChip({
  pressed,
  onPressedChange,
  onClick,
  ...props
}: ChipVariantProps &
  Omit<React.ComponentPropsWithRef<"button">, "aria-pressed"> & {
    pressed: boolean;
    onPressedChange?: (pressed: boolean) => void;
  }): React.JSX.Element {
  return (
    <Chip
      aria-pressed={pressed}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) onPressedChange?.(!pressed);
      }}
      {...props}
    />
  );
}

// The same chip as a label.
export function ChipLabel({
  variant,
  size = "sm",
  className,
  ...props
}: ChipVariantProps & React.ComponentPropsWithRef<"span">): React.JSX.Element {
  return (
    <span
      className={cn(chipVariants({ variant, size }), className)}
      data-slot="chip"
      {...props}
    />
  );
}
